"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden lg:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {menuItems.slice(0, 6).map(item => <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium transition-all duration-150 hover:!bg-transparent focus:!bg-transparent outline-none">
                  {item.label}
                  <span className="absolute bottom-1 left-1/2 h-[2px] w-0 bg-primary transition-all duration-150 ease-out group-hover:w-[calc(100%-2rem)] group-hover:left-4" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-md border-t border-border shadow-medium lg:hidden">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {menuItems.slice(0, 6).map(item => <Link key={item.id} to={item.href || "#"} className="px-4 py-2 text-sm font-medium transition-all duration-150 hover:bg-primary/10 rounded-md" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
            <Button asChild variant="outline" className="mt-2 border-2 hover:!bg-primary hover:!text-foreground-primary transition-all duration-80">
              <Link to="/#get-started">Get Started</Link>
            </Button>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-150 ease-out ${isScrolled ? "h-16 bg-white/80 backdrop-blur-md shadow-soft" : "h-[90px] bg-white/80 backdrop-blur-md"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541656/img/aj_dynasty_logo.png" alt="AJ Dynasty Smart Behavioral Solution" className="h-7" />
        </Link>

        <div className="flex items-center gap-4">
          <MenuProvider menu_id="3">
            <HeaderMenuConsumer />
          </MenuProvider>

          <Button asChild variant="outline" className="hidden lg:inline-flex border-2 hover:!bg-primary hover:!text-foreground-primary hover:!border-primary transition-all duration-80">
            <Link to="/#get-started">Get Started</Link>
          </Button>
        </div>
      </div>
    </header>;
}